import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Info, FileText, Clock, Users } from 'lucide-react';
import { API_ORIGIN } from '../services/api';

interface VerificationCheck {
  id: string;
  name: string;
  status: 'pass' | 'fail' | 'warning' | 'info';
  message: string;
  details?: any;
}

interface Signer {
  name: string;
  email: string;
  signedAt?: string;
  identityLevel?: string;
}

interface DocumentMetadata {
  createdAt?: string;
  completedAt?: string;
  signers?: Signer[];
}

interface VerificationResult {
  valid: boolean;
  documentId?: string;
  documentTitle?: string;
  checks: VerificationCheck[];
  summary: {
    overallStatus: 'valid' | 'invalid' | 'warning';
    totalChecks: number;
    passedChecks: number;
    failedChecks: number;
    warningChecks: number;
  };
  metadata?: DocumentMetadata;
}

function VerifyResultPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVerification();
  }, [documentId]);

  const fetchVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_ORIGIN}/verify/${documentId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Erreur lors de la vérification');
      }

      setResult(data.verification);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'fail':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-green-50 border-green-200';
      case 'fail':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getIdentityLevelLabel = (level?: string) => {
    switch (level) {
      case 'basic':
        return 'Basique (OTP email)';
      case 'enhanced':
        return 'Renforcé (SMS + email)';
      case 'substantial':
        return 'Substantiel (Vérification ID)';
      default:
        return 'Non spécifié';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/verify')}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retour au portail de vérification
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rapport de Vérification</h1>
              <p className="mt-1 text-sm text-gray-600">
                Document ID: {documentId}
              </p>
            </div>
            <button
              onClick={() => navigate('/verify')}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              ← Nouvelle vérification
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Overall Status */}
        <div
          className={`rounded-lg p-6 border-2 mb-6 ${
            result.summary.overallStatus === 'valid'
              ? 'bg-green-50 border-green-500'
              : result.summary.overallStatus === 'invalid'
              ? 'bg-red-50 border-red-500'
              : 'bg-yellow-50 border-yellow-500'
          }`}
        >
          <div className="flex items-center">
            {result.summary.overallStatus === 'valid' ? (
              <CheckCircle className="w-12 h-12 text-green-500 mr-4 flex-shrink-0" />
            ) : (
              <AlertCircle
                className={`w-12 h-12 mr-4 flex-shrink-0 ${
                  result.summary.overallStatus === 'invalid'
                    ? 'text-red-500'
                    : 'text-yellow-500'
                }`}
              />
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {result.summary.overallStatus === 'valid'
                  ? '✓ Document Authentique et Valide'
                  : result.summary.overallStatus === 'invalid'
                  ? '✗ Document Invalide'
                  : '⚠ Document Valide avec Avertissements'}
              </h2>
              {result.documentTitle && (
                <p className="text-gray-700 mt-1 font-medium">{result.documentTitle}</p>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-4 text-center">
            <div className="bg-white bg-opacity-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-900">
                {result.summary.totalChecks}
              </div>
              <div className="text-xs text-gray-600 mt-1">Vérifications</div>
            </div>
            <div className="bg-white bg-opacity-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-600">
                {result.summary.passedChecks}
              </div>
              <div className="text-xs text-gray-600 mt-1">Réussies</div>
            </div>
            <div className="bg-white bg-opacity-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-600">
                {result.summary.failedChecks}
              </div>
              <div className="text-xs text-gray-600 mt-1">Échouées</div>
            </div>
            <div className="bg-white bg-opacity-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-yellow-600">
                {result.summary.warningChecks}
              </div>
              <div className="text-xs text-gray-600 mt-1">Alertes</div>
            </div>
          </div>
        </div>

        {/* Document Metadata */}
        {result.metadata && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Informations du document
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center text-gray-600 mb-2">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Créé le:</span>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  {formatDate(result.metadata.createdAt)}
                </p>
              </div>
              <div>
                <div className="flex items-center text-gray-600 mb-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Complété le:</span>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  {formatDate(result.metadata.completedAt)}
                </p>
              </div>
            </div>

            {result.metadata.signers && result.metadata.signers.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center text-gray-600 mb-3">
                  <Users className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Signataires:</span>
                </div>
                <div className="space-y-3 ml-6">
                  {result.metadata.signers.map((signer, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="font-medium text-gray-900">{signer.name}</p>
                      <p className="text-sm text-gray-600">{signer.email}</p>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                          Signé le: {formatDate(signer.signedAt)}
                        </span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {getIdentityLevelLabel(signer.identityLevel)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Verification Checks */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Détails des vérifications
          </h3>
          <div className="space-y-3">
            {result.checks.map((check, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getStatusColor(check.status)}`}
              >
                <div className="flex items-start">
                  <div className="mr-3 mt-0.5">{getStatusIcon(check.status)}</div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{check.name}</h4>
                    <p className="text-sm text-gray-700 mt-1">{check.message}</p>
                    {check.details && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                          Détails techniques
                        </summary>
                        <pre className="mt-2 text-xs bg-white bg-opacity-50 p-2 rounded overflow-x-auto">
                          {JSON.stringify(check.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Valeur juridique
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              Ce rapport atteste de l'authenticité et de l'intégrité du document signé
              électroniquement.
            </p>
            <p>
              Au sens du règlement eIDAS (UE) n° 910/2014 (art. 25), une signature
              électronique ne peut être écartée comme preuve en justice au seul motif
              qu'elle est électronique. Sa force probante est appréciée par le juge au
              vu des éléments techniques de ce rapport.
            </p>
            <p className="font-medium">
              Note : seule la signature électronique qualifiée (QES) bénéficie de la
              présomption de fiabilité et de l'équivalence avec la signature manuscrite.
              Cette signature n'est pas une signature qualifiée.
            </p>
          </div>
        </div>

        {/* QR Code Placeholder */}
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <p className="text-sm text-gray-600 mb-4">
            Partagez ce rapport de vérification
          </p>
          <div className="inline-block p-4 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">URL de vérification:</p>
            <code className="text-xs bg-white px-3 py-1 rounded border border-gray-300">
              {window.location.href}
            </code>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>
            Vérifié par <span className="font-semibold">Juriseal</span> —
            signature électronique PAdES, règlement eIDAS
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Rapport généré le {new Date().toLocaleString('fr-FR')}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default VerifyResultPage;
